import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RecurrenceEditModeDialog, type RecurrenceEditMode } from '~/components/ui/RecurrenceEditModeDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Switch } from '~/components/ui/switch';
import { Textarea } from '~/components/ui/textarea';
import LocalDateTime, { type IANATimezone } from '~/lib/localdatetime';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

interface ShiftPlanEntryFormProps {
    storeId: number;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editEntryId?: string;
    prefillPractitionerId?: number;
    prefillDate?: string;
    selectedDay?: string;
}

const formatDate = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('yyyy-MM-dd') : '';
};

const formatTime = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('HH:mm') : '';
};

const parseDate = (dateStr: string, existingTime: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!dateStr) return null;
    const time = existingTime ? existingTime.format('HH:mm') : '00:00';
    return new LocalDateTime(`${dateStr} ${time}`, timezone);
};

const parseTime = (timeStr: string, existingDate: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!timeStr || !existingDate) return null;
    const date = existingDate.format('yyyy-MM-dd');
    return new LocalDateTime(`${date} ${timeStr}`, timezone);
};

const WEEKDAY_LABELS: Record<string, string> = {
    MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So',
};

export function ShiftPlanEntryForm({ storeId, open, onClose, onSuccess, editEntryId, prefillPractitionerId, prefillDate, selectedDay }: ShiftPlanEntryFormProps) {
    const userTimezone = (useUserStore(state => state.user?.timezone) ?? 'Europe/Berlin') as IANATimezone;
    const userRole = useUserStore(state => state.user?.role);
    const canManageShiftEntries = userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR;

    const [practitionerId, setPractitionerId] = useState<string>(prefillPractitionerId ? String(prefillPractitionerId) : '');
    const [type, setType] = useState<'AVAILABLE' | 'BLOCKED'>('AVAILABLE');
    const [blockedType, setBlockedType] = useState<'BREAK' | 'VACATION' | 'MEETING' | 'SICK_LEAVE' | 'EDUCATION' | 'OTHER'>('VACATION');
    const [description, setDescription] = useState('');
    const [startTimeObj, setStartTimeObj] = useState<LocalDateTime | null>(() => {
        if (prefillDate) {
            return new LocalDateTime(`${prefillDate} 08:00`, userTimezone);
        }
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(1, 'hour').utc().toDate(), userTimezone);
    });
    const [endTimeObj, setEndTimeObj] = useState<LocalDateTime | null>(() => {
        if (prefillDate) {
            return new LocalDateTime(`${prefillDate} 17:00`, userTimezone);
        }
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(2, 'hour').utc().toDate(), userTimezone);
    });
    const [isRecurring, setIsRecurring] = useState(false);
    const [frequency, setFrequency] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [interval, setIntervalValue] = useState(1);
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>({
        MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false,
    });
    const [recurrenceEndDate, setRecurrenceEndDate] = useState(dayjs().endOf('year').format('YYYY-MM-DD'));
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [originalSeriesStart, setOriginalSeriesStart] = useState<LocalDateTime | null>(null);
    const [originalSeriesEnd, setOriginalSeriesEnd] = useState<LocalDateTime | null>(null);
    const [recurrenceEditDialogOpen, setRecurrenceEditDialogOpen] = useState(false);
    const [recurrenceEditDialogIsDelete, setRecurrenceEditDialogIsDelete] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<Record<string, any> | null>(null);

    const { data: entryData, isLoading: isLoadingEntry, isFetching: isFetchingEntry } = trpc.shiftPlanEntries.getOne.useQuery(
        { id: editEntryId! },
        { enabled: !!editEntryId, staleTime: 0 }
    );

    const { data: practitionersData } = trpc.practitioners.getAll.useQuery(
        { page: 0, pageSize: 200 },
    );

    const practitioners = (practitionersData?.data ?? []).filter((p: any) =>
        p.practitioner_category && p.practitioner_category.id !== 1 && p.practitioner_category.id !== 2
    );

    const selectedPractitionerName = useMemo(() => {
        if (!practitionerId) return '';
        const p = practitioners.find((pr: any) => String(pr.id) === practitionerId);
        return p ? [p.title, p.firstName, p.lastName].filter(Boolean).join(' ') : '';
    }, [practitionerId, practitioners]);

    const createMutation = trpc.shiftPlanEntries.create.useMutation();
    const updateMutation = trpc.shiftPlanEntries.update.useMutation();
    const publishOneMutation = trpc.shiftPlanEntries.publishOne.useMutation();
    const deleteMutation = trpc.shiftPlanEntries.delete.useMutation();

    useEffect(() => {
        if (entryData && !dataLoaded && !isFetchingEntry) {
            setPractitionerId(String(entryData.practitionerId));
            setType(entryData.type as 'AVAILABLE' | 'BLOCKED');
            if (entryData.blockedType) setBlockedType(entryData.blockedType as typeof blockedType);
            setDescription(entryData.description ?? '');
            setIsRecurring(entryData.isRecurring);

            let parsedStart: LocalDateTime | null = null;
            let parsedEnd: LocalDateTime | null = null;

            if (entryData.startTimeLocal) {
                const stl = entryData.startTimeLocal as any;
                if (stl.localDatetime && stl.zone) {
                    parsedStart = new LocalDateTime(stl.localDatetime, stl.zone);
                }
            } else {
                const startDayjs = dayjs(entryData.startTime).tz(userTimezone);
                parsedStart = new LocalDateTime(startDayjs.format('YYYY-MM-DD HH:mm'), userTimezone);
            }

            if (entryData.endTimeLocal) {
                const etl = entryData.endTimeLocal as any;
                if (etl.localDatetime && etl.zone) {
                    parsedEnd = new LocalDateTime(etl.localDatetime, etl.zone);
                }
            } else {
                const endDayjs = dayjs(entryData.endTime).tz(userTimezone);
                parsedEnd = new LocalDateTime(endDayjs.format('YYYY-MM-DD HH:mm'), userTimezone);
            }

            if (parsedStart) setOriginalSeriesStart(parsedStart);
            if (parsedEnd) setOriginalSeriesEnd(parsedEnd);

            if (entryData.isRecurring && selectedDay && parsedStart && parsedEnd) {
                const occStartDt = new LocalDateTime(`${selectedDay} ${parsedStart.format('HH:mm')}`, parsedStart.getTimezone());
                const occEndDt = new LocalDateTime(`${selectedDay} ${parsedEnd.format('HH:mm')}`, parsedEnd.getTimezone());
                setStartTimeObj(occStartDt);
                setEndTimeObj(occEndDt);
            } else {
                if (parsedStart) setStartTimeObj(parsedStart);
                if (parsedEnd) setEndTimeObj(parsedEnd);
            }

            if (entryData.recurrenceRule) {
                const parts = entryData.recurrenceRule.split(';');
                for (const part of parts) {
                    const [key, val] = part.split('=');
                    if (key === 'FREQ') setFrequency(val as any);
                    if (key === 'INTERVAL') setIntervalValue(parseInt(val, 10));
                    if (key === 'BYDAY') {
                        const days = val.split(',');
                        setWeekdays(prev => {
                            const updated = { ...prev };
                            for (const d of days) updated[d] = true;
                            return updated;
                        });
                    }
                }
            }

            if (entryData.recurrenceEndDate) {
                setRecurrenceEndDate(dayjs(entryData.recurrenceEndDate).format('YYYY-MM-DD'));
            } else if (entryData.isRecurring) {
                setRecurrenceEndDate('');
            }

            setDataLoaded(true);
        }
    }, [entryData, dataLoaded, isFetchingEntry, userTimezone, selectedDay]);

    const derivedRecurrenceRule = useMemo(() => {
        if (!isRecurring) return '';
        let rule = `FREQ=${frequency};INTERVAL=${interval}`;
        if (frequency === 'WEEKLY') {
            const selectedDays = Object.entries(weekdays)
                .filter(([, v]) => v)
                .map(([k]) => k);
            if (selectedDays.length > 0) {
                rule += `;BYDAY=${selectedDays.join(',')}`;
            }
        }
        return rule;
    }, [isRecurring, frequency, interval, weekdays]);

    const canPublish = !!editEntryId && !!practitionerId && entryData?.isDraft === true;

    const handleSave = async () => {
        if (!canManageShiftEntries) return;
        if (!startTimeObj || !endTimeObj || !practitionerId) return;

        if (isRecurring && frequency === 'WEEKLY' && !Object.values(weekdays).some(v => v)) {
            toast.error("Error", { description: "Bitte mindestens einen Wochentag auswählen." });
            return;
        }

        if (editEntryId && isRecurring) {
            setPendingFormData({ action: 'save' });
            setRecurrenceEditDialogIsDelete(false);
            setRecurrenceEditDialogOpen(true);
            return;
        }

        await executeSave('all');
    };

    const executeSave = async (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!startTimeObj || !endTimeObj || !practitionerId) return;

        setIsSaving(true);
        try {
            const selectedSlotDay = selectedDay ?? null;

            let finalStartObj = startTimeObj;
            let finalEndObj = endTimeObj;

            if (editMode === 'all' && originalSeriesStart && originalSeriesEnd) {
                finalStartObj = new LocalDateTime(
                    `${originalSeriesStart.format('yyyy-MM-dd')} ${startTimeObj.format('HH:mm')}`,
                    originalSeriesStart.getTimezone()
                );
                finalEndObj = new LocalDateTime(
                    `${originalSeriesEnd.format('yyyy-MM-dd')} ${endTimeObj.format('HH:mm')}`,
                    originalSeriesEnd.getTimezone()
                );
            }

            const payload = {
                storeId,
                practitionerId: parseInt(practitionerId, 10),
                type: type as 'AVAILABLE' | 'BLOCKED',
                blockedType: type === 'BLOCKED' ? blockedType : null,
                description: description || undefined,
                startTimeLocalSerialized: finalStartObj.serialize(),
                endTimeLocalSerialized: finalEndObj.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring ? (recurrenceEndDate ? new Date(recurrenceEndDate).toISOString() : undefined) : undefined,
            };

            if (editEntryId) {
                await updateMutation.mutateAsync({
                    id: editEntryId,
                    ...payload,
                    editMode,
                    selectedSlotDay,
                });
            } else {
                await createMutation.mutateAsync(payload);
            }
            onSuccess();
        } catch (e) {
            console.error('Error saving draft entry:', e);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!canManageShiftEntries) return;
        if (!editEntryId) return;
        setIsPublishing(true);
        try {
            await publishOneMutation.mutateAsync({ id: editEntryId });
            onSuccess();
        } catch (e) {
            console.error('Error publishing entry:', e);
        } finally {
            setIsPublishing(false);
        }
    };

    const handleDelete = async () => {
        if (!canManageShiftEntries) return;
        if (!editEntryId) return;

        if (isRecurring) {
            setPendingFormData({ action: 'delete' });
            setRecurrenceEditDialogIsDelete(true);
            setRecurrenceEditDialogOpen(true);
            return;
        }

        await executeDelete('all');
    };

    const executeDelete = async (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!editEntryId) return;
        const selectedSlotDay = selectedDay ?? null;
        try {
            await deleteMutation.mutateAsync({
                id: editEntryId,
                editMode,
                selectedSlotDay,
            });
            onSuccess();
        } catch (e) {
            console.error('Error deleting entry:', e);
        }
    };

    const handleRecurrenceEditModeSelect = (mode: RecurrenceEditMode) => {
        setRecurrenceEditDialogOpen(false);
        if (pendingFormData?.action === 'delete') {
            executeDelete(mode);
        } else {
            executeSave(mode);
        }
        setPendingFormData(null);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{editEntryId ? 'Entwurf bearbeiten' : 'Neuer Entwurf'}</DialogTitle>
                </DialogHeader>

                {editEntryId && (isLoadingEntry || isFetchingEntry || !dataLoaded) ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                <>
                {editEntryId && entryData?.isDraft && (
                    <div className="bg-orange-100 border border-orange-300 text-orange-800 rounded-md px-4 py-3 text-sm font-medium">
                        Dieser Eintrag ist ein Entwurf und noch nicht veröffentlicht.
                    </div>
                )}
                <div className="space-y-4 py-4">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm text-muted-foreground">
                            Ausgewählt: <b>{selectedPractitionerName}</b>
                        </span>
                    </div>

                    <div className="flex items-end gap-6">
                        <div className="space-y-2">
                            <Label>Datum *</Label>
                            <Input
                                type="date"
                                className="w-[140px]"
                                value={formatDate(startTimeObj)}
                                disabled={!canManageShiftEntries}
                                onChange={e => {
                                    setStartTimeObj(parseDate(e.target.value, startTimeObj, userTimezone));
                                    setEndTimeObj(parseDate(e.target.value, endTimeObj, userTimezone));
                                }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Startzeit *</Label>
                            <Input
                                type="time"
                                className="w-[100px]"
                                value={formatTime(startTimeObj)}
                                disabled={!canManageShiftEntries}
                                onChange={e => setStartTimeObj(parseTime(e.target.value, startTimeObj, userTimezone))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Endzeit *</Label>
                            <Input
                                type="time"
                                className="w-[100px]"
                                value={formatTime(endTimeObj)}
                                disabled={!canManageShiftEntries}
                                onChange={e => setEndTimeObj(parseTime(e.target.value, endTimeObj, userTimezone))}
                            />
                        </div>
                    </div>

                    <div className="flex items-end gap-2">
                        <div className="space-y-2">
                            <Label>Typ</Label>
                            <Select value={type} onValueChange={(v) => setType(v as 'AVAILABLE' | 'BLOCKED')} disabled={!canManageShiftEntries || !!editEntryId}>
                                <SelectTrigger className={type === 'AVAILABLE' ? 'border-green-400' : 'border-red-400'}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="AVAILABLE">Verfügbarkeit</SelectItem>
                                    <SelectItem value="BLOCKED">Blocker</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {type === 'BLOCKED' && (
                            <div className="space-y-2">
                                <Label>Blocker Grund</Label>
                                <Select value={blockedType} onValueChange={(v) => setBlockedType(v as typeof blockedType)} disabled={!canManageShiftEntries}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="BREAK">Pause</SelectItem>
                                        <SelectItem value="VACATION">Urlaub</SelectItem>
                                        <SelectItem value="MEETING">Meeting</SelectItem>
                                        <SelectItem value="SICK_LEAVE">Krank</SelectItem>
                                        <SelectItem value="EDUCATION">Fortbildung</SelectItem>
                                        <SelectItem value="OTHER">Sonstiges</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>Beschreibung (optional)</Label>
                        <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} disabled={!canManageShiftEntries} />
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch checked={isRecurring} onCheckedChange={setIsRecurring} disabled={!canManageShiftEntries || !!editEntryId} />
                        <Label>Wiederkehrend</Label>
                    </div>

                    {isRecurring && (
                        <div className="space-y-4 border rounded-md p-3 bg-muted/30">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Frequenz</Label>
                                    <Select value={frequency} onValueChange={(v) => setFrequency(v as any)} disabled={!canManageShiftEntries || !!editEntryId}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="DAILY">Täglich</SelectItem>
                                            <SelectItem value="WEEKLY">Wöchentlich</SelectItem>
                                            <SelectItem value="MONTHLY">Monatlich</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Intervall (alle x Tage/Wochen/Monate)</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={interval}
                                        onChange={e => setIntervalValue(parseInt(e.target.value, 10) || 1)}
                                        disabled={!canManageShiftEntries}
                                    />
                                </div>
                            </div>

                            {frequency === 'WEEKLY' && (
                                <div className="space-y-2">
                                    <Label>Wochentage</Label>
                                    <div className="flex gap-2 flex-wrap">
                                        {Object.entries(WEEKDAY_LABELS).map(([key, label]) => (
                                            <label key={key} className="flex items-center gap-1 text-sm">
                                                <Checkbox
                                                    checked={weekdays[key]}
                                                    onCheckedChange={(checked) =>
                                                        setWeekdays(prev => ({ ...prev, [key]: !!checked }))
                                                    }
                                                    disabled={!canManageShiftEntries}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                    {!Object.values(weekdays).some(selected => selected) && (
                                        <p className="text-xs text-destructive">Bitte mindestens einen Tag auswählen.</p>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Enddatum</Label>
                                <Input
                                    type="date"
                                    value={recurrenceEndDate}
                                    onChange={e => setRecurrenceEndDate(e.target.value)}
                                    disabled={!canManageShiftEntries}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    {canManageShiftEntries && editEntryId && (
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={isSaving || isPublishing || deleteMutation.isPending}
                            className="mr-auto"
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Löschen
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    {canManageShiftEntries && canPublish && (
                        <Button
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={handlePublish}
                            disabled={isPublishing || isSaving}
                        >
                            {isPublishing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Veröffentlichen
                        </Button>
                    )}
                    {canManageShiftEntries && (
                        <Button
                            onClick={handleSave}
                            disabled={isSaving || isPublishing || !practitionerId || !startTimeObj || !endTimeObj}
                        >
                            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editEntryId ? 'Speichern' : 'Erstellen'}
                        </Button>
                    )}
                </DialogFooter>
                </>
                )}
            </DialogContent>

            <RecurrenceEditModeDialog
                open={recurrenceEditDialogOpen}
                isDelete={recurrenceEditDialogIsDelete}
                onSelect={handleRecurrenceEditModeSelect}
                onCancel={() => setRecurrenceEditDialogOpen(false)}
                occurrenceLabel={startTimeObj?.format('EEEE, dd.MM.yyyy')}
                occurrenceDate={startTimeObj?.format('dd.MM.yyyy')}
                seriesStartDate={originalSeriesStart?.format('dd.MM.yyyy')}
                seriesEndDate={recurrenceEndDate ? dayjs(recurrenceEndDate).format('DD.MM.YYYY') : undefined}
            />
        </Dialog>
    );
}
